// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  ITukGetAllAttrs,
  ITukGetAttrs,
  ITukSaveAttrs,
} from 'interfaces/back-office/content-management/tuk/tuk/tuk-view.interface'
import {
  ITukApiPaginatedResponse,
  ITukApiGetAllResponse,
  ITukApiGetResponse,
  ITukApiSaveResponse,
} from 'interfaces/back-office/content-management/tuk/tuk/tuk-api.interface'
import { ITukDetail } from 'interfaces/back-office/content-management/tuk/tuk'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Lodash
import { withHelper_baseResponsePaginationTypes, withHelper_tukList, withHelper_tuk } from 'models/extensions/with-helper'

// Utils
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Tuk List Model
const TukList = types.model('TukListPaginated').props(withHelper_baseResponsePaginationTypes(withHelper_tukList()))

// Tuk Detail Model
const TukDetail = withHelper_tuk()

const TukStoreModel = types
  .model('Tuk')
  .props({
    contentTuk_isLoading: types.optional(types.boolean, false),
    contentTuk_isSaveLoading: types.optional(types.boolean, false),
    contentTuk_list: types.optional(TukList, {}),
    contentTuk_detail: types.optional(TukDetail, {}),
  })
  .actions(self => ({
    contentTuk_SET_LOADING: (payload: boolean): void => {
      self.contentTuk_isLoading = payload
    },
    contentTuk_SET_SAVE_LOADING: (payload: boolean): void => {
      self.contentTuk_isSaveLoading = payload
    },
    contentTuk_SET_LIST: (payload: ITukApiPaginatedResponse): void => {
      self.contentTuk_list = cast({
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
          }
        }),
      })
    },
    contentTuk_SET_DETAIL: (payload: ITukDetail): void => {
      self.contentTuk_detail = cast(payload)
    },
    contentTuk_CLEAR_LIST: (): void => {
      self.contentTuk_list = cast({})
    },
    contentTuk_CLEAR_DETAIL: (): void => {
      self.contentTuk_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch list
     *
     * @param {ITukGetAllAttrs} payload
     *
     * @return {any} any
     */
    contentTuk_fetchList: flow(function* (payload: ITukGetAllAttrs) {
      self.contentTuk_SET_LOADING(true)

      const response: ITukApiGetAllResponse = yield self.environment.contentTukApi.contentTuk_getTukList({
        requestConfig: { ...payload.requestConfig },
        body: { ...payload.body },
      })
      if (response.ok) self.contentTuk_SET_LIST(response.res.data)

      self.contentTuk_SET_LOADING(false)
    }),

    /**
     * @description Fetch tuk
     *
     * @param {ITukGetAttrs} payload
     *
     * @return {any} any
     */
    contentTuk_fetch: flow(function* (payload: ITukGetAttrs) {
      self.contentTuk_SET_LOADING(true)

      const response: ITukApiGetResponse = yield self.environment.contentTukApi.contentTuk_getTuk({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.contentTuk_SET_DETAIL(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.contentTuk_SET_LOADING(false)
    }),

    /**
     * @description Save tuk
     *
     * @param {ITukSaveAttrs} payload
     *
     * @return {any} any
     */
    contentTuk_save: flow(function* (payload: ITukSaveAttrs) {
      self.contentTuk_SET_SAVE_LOADING(true)

      const response: ITukApiSaveResponse = yield self.environment.contentTukApi.contentTuk_saveTuk({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess(response.res.data)
      }

      self.contentTuk_SET_SAVE_LOADING(false)
    }),
  }))

export const createTukStoreDefaultModel = () => types.optional(TukStoreModel, {})
